import BigNumber from "bignumber.js";
import { ProductStatusEnum, TYPE_OF_ATTRIBUTE, TYPE_OF_PRODUCTS } from "../../constants/product";
import { getNormalAttributes, getVariantDefault } from "./product";

export const calculateCostPrice = (price: string | number, markup: number) => {
  const costPrice = Number(price || 0) / (1 + markup / 100);

  const bigInt = new BigNumber(costPrice);

  return bigInt.toFixed(2, BigNumber.ROUND_DOWN);
};

export const calculateSellingPrice = (price: string | number, markup: number) => {
  const sellingPrice = Number(price || 0) * (1 + markup / 100);

  const bigInt = new BigNumber(sellingPrice);

  return bigInt.toFixed(2, BigNumber.ROUND_UP);
};

export const getAttributesValueFromProductDetail = (
  markup: number,
  defaultAttributes: Attribute[] = [],
  product?: Product | null
): { price?: string; quantity?: string; attributes: any; costPrice?: string } => {
  if (!product) {
    return {
      price: "",
      costPrice: "",
      attributes: defaultAttributes.map(item => ({
        ...item,
        quantifiable: false,
        attribute_name: item.name,
        variants: [{ attribute_value: "", price: "" }],
        type_of_attribute: item.is_required
          ? TYPE_OF_ATTRIBUTE.is_required
          : item.is_multiple_choice
          ? TYPE_OF_ATTRIBUTE.is_multiple_choice
          : "",
      })),
    };
  }
  const defaultVariant = getVariantDefault(product);
  const attributes = getNormalAttributes(product, markup);

  const costPrice = Number(defaultVariant?.price || 0) / (1 + markup / 100);

  const bigInt = new BigNumber(costPrice);

  return {
    price: defaultVariant?.price.toString(),
    // costPrice: bigInt.toFixed(2, BigNumber.ROUND_UP),
    costPrice: calculateCostPrice(defaultVariant?.price, markup),
    attributes: attributes.map(attribute => ({
      ...attribute,
      type_of_attribute: attribute.is_required
        ? TYPE_OF_ATTRIBUTE.is_required
        : attribute.is_multiple_choice
        ? TYPE_OF_ATTRIBUTE.is_multiple_choice
        : "",
    })),
  };
};

export const getValuesFromProductDetail = (
  markup: number,
  product?: Product | null,
  merchantStoreId?: string | number,
  defaultAttributes?: Attribute[]
): CreateUpdateProductValues => {
  defaultAttributes?.sort((a, b) => a.id - b.id);

  const { price, attributes, costPrice } = getAttributesValueFromProductDetail(
    markup,
    product?.id ? [] : defaultAttributes,
    product
  );

  return {
    groupMenuId: product?.groupMenuId || "",
    merchant_store_id: merchantStoreId?.toString() || product?.merchant_store_id || "",
    canPreOrder: product?.canPreOrder || false,
    price: Number(price).toFixed(2) || "",
    costPrice: Number(costPrice).toFixed(2) || "",
    category_id: product?.category_id?.toString() || null,
    name: product?.name || "",
    description: product?.description || "",
    thumbnail: product?.thumbnail || "",
    attributes: attributes,
    images: product?.images || [],
    videos: product?.videos || [],
    brand: product?.brand || "",
    manufacturer: product?.manufacturer || "",
    manufacturer_address: product?.manufacturer_address || "",
    product_warranty: (product?.warranty as PRODUCT_WARRANTY) || "",
    warranty: product?.warranty || "",
    width: product?.width?.toString() || "",
    height: product?.height?.toString() || "",
    depth: product?.depth?.toString() || "",
    condition: product?.condition?.toString() || "",
    day_to_prepare_order: product?.day_to_prepare_order?.toString() || "",
    lead_time: product?.lead_time?.toString() || "",
    sku: product?.sku || "",
    status: product?.status?.toString() || "",
    settings: {
      is_featured: true,
    },
    main_tags: !!product?.main_tags?.length ? product?.main_tags[0] : null,
    sub_tags: product?.sub_tags || [],
    shipment_weight: product?.shipment_weight?.toString() || undefined,
    type: product?.type || TYPE_OF_PRODUCTS.FOOD,
    quantity: product?.type === TYPE_OF_PRODUCTS.RETAIL ? product?.quantity : undefined,
    barcode: product?.barcode || "",
  };
};

export const getBodyAttributesFromValuesTypeRadioBox = (values: CreateUpdateProductValues): ProductAttribute[] => {
  const attributes: ProductAttribute[] = values.attributes.map(item => ({
    ...item,
    is_required: false, //hard code, remove later
    is_multiple_choice: true,
    variants: item.variants
      .filter(item => item)
      .map(variant => ({
        ...variant,
        price: Number(variant.price),
        maximumAllowQuantity: Number(variant.maximumAllowQuantity),
      })),
    min: Number(item.min),
    max: Number(item.max),
    quantifiable: item.quantifiable ? item.quantifiable : false,
    type_of_attribute: [TYPE_OF_ATTRIBUTE.is_multiple_choice],
  }));
  const requiredAttribute = attributes.find(item => item.is_required);
  const defaultAttribute = [
    {
      attribute_name: "default",
      is_required: true,
      is_multiple_choice: false,
      variants: [{ attribute_value: "default", price: Number(values.price), isSoldOut: false }],
      min: 1,
      max: 1,
      quantifiable: false,
    },
  ];
  return [...attributes, ...(requiredAttribute ? [] : defaultAttribute)];
};

export const getBodyFromProductValues = (
  isSellingMode: boolean,
  values: CreateUpdateProductValues,
  merchantStoreId?: string
): CreateUpdateProductBody => {
  return {
    groupMenuId: values.groupMenuId,
    isSellingMode: isSellingMode,
    merchant_store_id: merchantStoreId || values.merchant_store_id,
    price: Number(values.price),
    category_id: Number(values.category_id),
    name: values.name || "",
    canPreOrder: values.canPreOrder,
    description: values.description || "",
    attributes: getBodyAttributesFromValuesTypeRadioBox(values),
    images: values.images || [],
    videos: values.videos || [],
    thumbnail: values.images?.[0],
    brand: values.brand || "",
    manufacturer: values.manufacturer || "",
    manufacturer_address: values.manufacturer_address || "",
    product_warranty: (values.warranty as PRODUCT_WARRANTY) || "",
    warranty: values.warranty || "",
    shipment_weight: Number(values.shipment_weight),
    width: Number(values.width),
    height: Number(values.height),
    depth: Number(values.depth),
    condition: 1,
    day_to_prepare_order: Number(values.day_to_prepare_order) || 1,
    lead_time: Number(values.lead_time),
    sku: values.sku,
    status: Number(values.status),
    settings: {
      is_featured: true,
    },
    main_tags: values.main_tags ? [values.main_tags] : [],
    sub_tags: values.sub_tags,
    type: TYPE_OF_PRODUCTS.FOOD,
    quantity: values.type === TYPE_OF_PRODUCTS.RETAIL ? Number(values.quantity) : undefined,
    barcode: values.barcode || undefined,
  };
};

export const getProductCountByStatus = (
  productCounts: { status: number; count: number }[],
  status: ProductStatusEnum
) => {
  const productCount = productCounts.find(item => item.status === status);
  return productCount?.count || 0;
};

export const createNewAttribute = (): ProductAttributeValues => ({
  attribute_name: "",
  type_of_attribute: TYPE_OF_ATTRIBUTE.is_multiple_choice,
  max: 1,
  min: 0,
  variants: [
    {
      attribute_value: "",
      price: "",
      isSoldOut: false,
    },
  ],
  quantifiable: false,
});
